import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { usePrevious } from "../../components/CustomHooks";
import { AppContext } from "../../App";
import useApi from "../../api";
import { calculateValueFromEvent, toComboValues } from "../../components/Utils";
import { Box, Button, Chip, Grid, IconButton, Paper, Toolbar, Tooltip, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import ComboSelect from "../../components/ComboSelect";
import TextField from "../../components/TextField";
import { isNil, throttle } from "lodash";
import TableLoader from "../../components/TableLoader";
import EmptyStateLabel from "../../components/EmptyStateLabel";

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { consultarNodos } from "../../api/nodos";

export const ESTATUS_NODO = [
    { id: 'TODOS', nombre: 'Todos' },
    { id: 'CREANDO', nombre: 'Creando' },
    { id: 'INICIALIZANDO', nombre: 'Inicializando' },
    { id: 'LISTO', nombre: 'Listo' },
    { id: 'APAGANDO', nombre: 'Apagando' },
    { id: 'APAGADO', nombre: 'Apagado' },
    { id: 'CON_ERROR', nombre: 'Con error' },
    { id: 'TERMINADO', nombre: 'Terminado' },
]

export const SERVICIO_CLOUD = [
    { id: 'TODOS', nombre: 'Todos' },
    { id: 'AWS', nombre: 'Amazon Web Service' },
    { id: 'GOOGLE', nombre: 'Google Cloud Computing' },
    { id: 'AZURE', nombre: 'Microsoft Azure' },
]

// Servicios de nube seleccionables al registrar un nodo (solo AWS soportado hoy)
export const SERVICIO_NODO = [
    { id: 'AWS', nombre: 'Amazon Web Service' },
    { id: 'GOOGLE', nombre: 'Google Cloud Computing', disabled: true },
    { id: 'AZURE', nombre: 'Microsoft Azure', disabled: true },
]

// El nodo con su id en admin y, si lo tiene, el de su host en la nube (p.ej. i-0a254c5eae77ff562)
export const idsNodo = (nodo) => `#${nodo.id}${nodo.cloudInstanceId ? ` · ${nodo.cloudInstanceId}` : ''}`;

// Opciones para elegir el nodo de una instancia (GET /admin/nodos/disponibles): con las instancias que ya
// hospeda cada uno, para no mandar una instancia a un nodo que ya está cargado
export const toNodosComboValues = (nodos) => nodos && nodos.length > 0
    ? nodos.map(n => {
        const instancias = (n.instancias || [])
            .filter(i => i.estatus !== 'ELIMINADA' && i.cliente)
            .map(i => i.cliente.siteName);
        return {
            value: n.id,
            text: `${idsNodo(n)} · ${n.ipAddress || 's/IP'} · ${n.size} · espacio: ${n.espacioDisponible}${n.reservado ? ' · (Reservado)' : ''}`
                + (instancias.length > 0 ? ` · ${instancias.join(', ')}` : ''),
        };
    }) : [];

export const SIZE_NODO = [
    { id: 'T2_SMALL', nombre: 'T2 Small' },
    { id: 'T2_MEDIUM', nombre: 'T2 Medium' },
    { id: 'T2_LARGE', nombre: 'T2 Large' },
]


const defaultData = {
    instancia: '',
    servicio: 'AWS',
    estatus: 'LISTO',
}

export default function ListadoNodos({ ...props }) {
    const { reload } = props;
    const navigate = useNavigate();
    const { tieneCuentaPermisos } = useContext(AppContext);
    const puedeAdministrar = tieneCuentaPermisos(['PERM_ADMIN_CLIENTES']);

    const [data, setData] = useState(defaultData);
    const [nodos, setNodos] = useState(null);
    const prevData = usePrevious(data);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const prevDataPM = usePrevious(paginationModel);

    const [rowCount, setRowCount] = useState(0);

    // El nombre de la instancia se busca mientras se escribe, sin disparar una consulta por tecla
    const [query, setQuery] = useState(null);
    const handleInputThrottled = useRef(throttle((query) => setQuery(query), 1000));


    const [{ data: nodosData, loading: loadingNodos }, nodosApi] = useApi();

    useEffect(() => {
        if (reload) {
            realizarConsultaNodos();
        }
    }, [reload]);

    useEffect(() => {
        realizarConsultaNodos();
    }, []);

    useEffect(() => {
        if (nodosData) {
            setRowCount(nodosData.totalElements);
            setNodos(nodosData.content || [])
        }
    }, [nodosData]);

    useEffect(() => {
        if (isNil(query) || (query !== '' && query.trim().length < 2)) {
            return;
        }
        realizarConsultaNodos(true);
    }, [query]);

    useEffect(() => {
        if (prevData && (data.servicio !== prevData.servicio || data.estatus !== prevData.estatus)) {
            realizarConsultaNodos();
        }
    }, [data.servicio, data.estatus]);

    useEffect(() => {
        if (prevDataPM && (paginationModel.page !== prevDataPM.page || paginationModel.pageSize !== prevDataPM.pageSize)) {
            realizarConsultaNodos();
        }
    }, [paginationModel]);

    const realizarConsultaNodos = (resetPM = false) => {
        setNodos(null);
        if (resetPM) {
            setPaginationModel({
                page: 0,
                pageSize: 10,
            });
        }
        const filtros = {
            ...data,
            servicio: data.servicio === 'TODOS' ? null : data.servicio,
            estatus: data.estatus === 'TODOS' ? null : data.estatus,
            instancia: data.instancia.trim() === '' ? null : data.instancia.trim(),
        }
        nodosApi(consultarNodos(filtros, paginationModel));
    }

    const handleChangeValue = name => event => {
        const value = calculateValueFromEvent(event);

        setData({
            ...data,
            [name]: value
        });
    }

    const detalleNodo = useCallback(
        (row) => () => {
            navigate(`/nodos/${row.id}`);
        },
        [navigate],
    );

    const columns = useMemo(() => [
        {
            field: 'cloudInstanceId', headerName: 'Id', flex: 1, minWidth: 195,
            renderCell: (params) => (
                <Tooltip title={params.row.cloudInstanceId}><Link to={`/nodos/${params.row.id}`} state={{ cliente: params.row }}>{params.row.cloudInstanceId}</Link></Tooltip>
            )
        },
        { field: 'servicio', headerName: 'Servicio', flex: 1, minWidth: 65 },
        { field: 'estatus', headerName: 'Estatus', flex: 1, minWidth: 150 },
        {
            field: 'reservado', headerName: 'Reservado', flex: 1, minWidth: 110,
            renderCell: (params) => {
                if (params.row.reservado) {
                    return (
                        <Tooltip title="Nodo reservado: excluido de la asignación automática de instancias">
                            <Chip icon={<LockIcon />} label="Reservado" size="small" color="warning" variant="outlined" />
                        </Tooltip>
                    );
                }
                return params.row.espacioDisponible > 0
                    ? <Chip label="Disponible" size="small" color="success" variant="outlined" />
                    : <Chip label="Ocupado" size="small" color="default" variant="outlined" />;
            }
        },
        { field: 'espacioDisponible', headerName: 'Slots', flex: 1, minWidth: 75 },
        {
            field: 'instanciasActivas', headerName: 'I. Activas', flex: 1, minWidth: 75,
            renderCell: (params) => params.row.instancias ? params.row.instancias.filter(i => i.activa).length : 0
        },
        {
            field: 'instanciasInactivas', headerName: 'I. Inactivas', flex: 1, minWidth: 75,
            renderCell: (params) => params.row.instancias ? params.row.instancias.filter(i => !i.activa).length : 0
        },
        {
            field: 'ipAddress', headerName: 'IP Address', flex: 1, minWidth: 120
        },
        {
            field: 'actions', headerName: 'Acciones', type: 'actions', width: 95,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<Tooltip title="Ver detalle nodo"><ArrowForwardIcon /></Tooltip>}
                    label="Ver detalle"
                    onClick={detalleNodo(params.row)}
                />,
            ]
        }
    ], [detalleNodo]);

    return (
        <>
            <Outlet />
            <Toolbar disableGutters sx={{ gap: 2 }}>
                <Typography sx={{ flex: 1 }}>Nodos</Typography>
                {puedeAdministrar &&
                    <Button
                        onClick={() => navigate('/nodos/nuevo')}
                        startIcon={<AddIcon />}
                        variant="contained" color="primary">Registrar nodo</Button>
                }
                <Tooltip title="Actualizar lista de nodos">
                    <span>
                        <IconButton
                            color="primary"
                            disabled={loadingNodos}
                            onClick={() => realizarConsultaNodos(true)}>
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Toolbar>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Instancia (p.ej. ifo, playground)"
                        field="instancia"
                        value={data.instancia}
                        onChange={(e) => {
                            handleInputThrottled.current(calculateValueFromEvent(e));
                            handleChangeValue('instancia')(e);
                        }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ComboSelect
                        label="Servicio"
                        field="servicio"
                        value={data.servicio}
                        onChange={handleChangeValue('servicio')}
                        options={toComboValues(SERVICIO_CLOUD)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ComboSelect
                        label="Estatus nodo"
                        field="estatus"
                        value={data.estatus}
                        onChange={handleChangeValue('estatus')}
                        options={toComboValues(ESTATUS_NODO)}
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
                {loadingNodos &&
                    <TableLoader columns={columns} rowsNum={5} />
                }
                {nodos && nodos.length === 0 &&
                    <EmptyStateLabel text="No hay nodos con esos criterios" />
                }
                {nodos && nodos.length > 0 &&
                    <Paper elevation={0} sx={{ width: '100%', borderRadius: 6 }}>
                        <DataGrid
                            autoHeight
                            sx={{ borderRadius: 6 }}
                            disableRowSelectionOnClick
                            columns={columns}
                            rows={nodos}
                            loading={loadingNodos}
                            rowCount={rowCount}
                            pageSizeOptions={[10, 20, 50, 100]}
                            paginationModel={paginationModel}
                            paginationMode="server"
                            onPaginationModelChange={setPaginationModel}
                            onRowDoubleClick={(params, event) => {
                                //navigate(`/nodos/${params.row.id}`, { state: { cliente: params.row } });
                                //notificar('Cliente seleccionado: ' + params.row.nombre);
                            }}
                        />
                    </Paper>
                }
            </Box>
        </>
    );
}